import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { enterSpace } from '~src/app/actions/spaces/EnterSpaceAction';
import { MapDispatchToProps } from '~src/app/utils/ConnectUtils';

import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { AllServerUsersSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '../../../state/SpaceInfo';
import { ConnectedQueryParamUpdateProvider } from '../../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../../SpacesSiteUrls';
import { AllServerUsersAndIdentitiesSpaceOwnProps } from './AllServerUsersAndIdentitiesSpace';
import { ConnectedAllServerUsersAndIdentitiesSpace } from './ConnectedAllServerUsersAndIdentitiesSpace';

export interface AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly subspaceName: AllServerUsersSubspaceName;

  readonly renderSpace: (props: AllServerUsersAndIdentitiesSpaceOwnProps) => JSX.Element;
}

export interface AllServerUsersAndIdentitiesSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
}

export type AllServerUsersAndIdentitiesSpaceProviderContainerProps = AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps & AllServerUsersAndIdentitiesSpaceProviderDispatchProps;

export class AllServerUsersAndIdentitiesSpaceProviderContainer extends React.PureComponent<AllServerUsersAndIdentitiesSpaceProviderContainerProps> {
  public render() {
    return this.props.renderSpace(this.props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: AllServerUsersAndIdentitiesSpaceProviderContainerProps) {
    // This is a workaround because ConnectedSpaceInfoProvider does not have logic for enter space on component update. 
    if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'serverusers',
        subspaceName: this.props.subspaceName
      }
    );
    this.props.requestSpaceContents();
  }
}

const mapDispatchToProps: MapDispatchToProps<AllServerUsersAndIdentitiesSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
}, dispatch);

export const ConnectedAllServerUsersAndIdentitiesSpaceProviderContainer: React.ComponentClass<AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps>
  = connect(undefined, mapDispatchToProps)(AllServerUsersAndIdentitiesSpaceProviderContainer);

const renderSpace = (props: AllServerUsersAndIdentitiesSpaceOwnProps): JSX.Element => <ConnectedAllServerUsersAndIdentitiesSpace {...props} />;

export interface AllServerUsersAndIdentitiesSpaceProviderOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}
export type AllServerUsersAndIdentitiesSpaceProviderProps = AllServerUsersAndIdentitiesSpaceProviderOwnProps & Omit<AllServerUsersAndIdentitiesSpaceProviderContainerOwnProps, 'renderSpace'>;

export const AllServerUsersAndIdentitiesSpaceProvider: React.FunctionComponent<AllServerUsersAndIdentitiesSpaceProviderProps> = (props) => {

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: 'serverusers',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider
        routeLocationPath={props.routeLocationPath}
        routeLocationSearch={props.routeLocationSearch}
        shouldUpdateBreadcrumbs={true}
        shouldUpdateContentTypes={false}
        shouldUpdateFilters={true}
        shouldUpdateSort={true}
      >
        <ConnectedAllServerUsersAndIdentitiesSpaceProviderContainer {...props} renderSpace={renderSpace}/>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
};
